import { FullscreenOutlined, ZoomInOutlined, ZoomOutOutlined } from "@ant-design/icons";
import { Button, Flex, Select, Tooltip } from "../../../../../shared/frontend/ui";
import React from "react";
import { spacing } from "../app/design-system";
import { useI18n } from "../app/i18n/useI18n";

/** Zoomfaktor 1 entspricht 100 %. */
const ZOOM_PERCENT_SCALE = 100;
const ZOOM_MATCH_TOLERANCE = 0.01;

const DEFAULT_DOCUMENT_ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2];
const DEFAULT_DOCUMENT_MIN_ZOOM = 0.5;
const DEFAULT_DOCUMENT_MAX_ZOOM = 2.5;
const DEFAULT_DOCUMENT_ZOOM_STEP = 0.25;

export type DocumentZoomControlProps = {
  zoom: number;
  onZoomChange: (zoom: number) => void;
  zoomSteps?: number[];
  minZoom?: number;
  maxZoom?: number;
  step?: number;
  size?: "small" | "middle" | "large";
  disabled?: boolean;
  showReset?: boolean;
  onReset?: () => void;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * Zentrale Zoom- und Lupenleiste für Dokument-Editoren und Vorschauen.
 *
 * Enthält Verkleinern, Prozentauswahl, Vergrößern und optionalen Reset,
 * damit die Zoom-Bedienung im gesamten System einheitlich bleibt.
 */
export function DocumentZoomControl({
  zoom,
  onZoomChange,
  zoomSteps = DEFAULT_DOCUMENT_ZOOM_STEPS,
  minZoom = DEFAULT_DOCUMENT_MIN_ZOOM,
  maxZoom = DEFAULT_DOCUMENT_MAX_ZOOM,
  step = DEFAULT_DOCUMENT_ZOOM_STEP,
  size = "small",
  disabled = false,
  showReset = false,
  onReset,
  className,
  style,
}: DocumentZoomControlProps) {
  const { text } = useI18n();

  const handleZoomOut = () => {
    const lowerSteps = zoomSteps.filter((s) => s < zoom - ZOOM_MATCH_TOLERANCE);
    const next = lowerSteps.length > 0
      ? lowerSteps[lowerSteps.length - 1]
      : Math.max(minZoom, Math.round((zoom - step) * ZOOM_PERCENT_SCALE) / ZOOM_PERCENT_SCALE);
    onZoomChange(Math.max(minZoom, next));
  };

  const handleZoomIn = () => {
    const higherSteps = zoomSteps.filter((s) => s > zoom + ZOOM_MATCH_TOLERANCE);
    const next = higherSteps.length > 0
      ? higherSteps[0]
      : Math.min(maxZoom, Math.round((zoom + step) * ZOOM_PERCENT_SCALE) / ZOOM_PERCENT_SCALE);
    onZoomChange(Math.min(maxZoom, next));
  };

  const handleReset = () => {
    if (onReset) onReset();
    else onZoomChange(1);
  };

  const options = React.useMemo(() => {
    const values = [...zoomSteps];
    const roundedZoom = Math.round(zoom * ZOOM_PERCENT_SCALE) / ZOOM_PERCENT_SCALE;
    if (!values.some((v) => Math.abs(v - roundedZoom) < ZOOM_MATCH_TOLERANCE)) {
      values.push(roundedZoom);
      values.sort((a, b) => a - b);
    }
    return values.map((val) => ({
      value: val,
      label: `${Math.round(val * ZOOM_PERCENT_SCALE)} %`,
    }));
  }, [zoom, zoomSteps]);

  return (
    <Flex gap={spacing.xs} align="center" className={className} style={style}>
      <Tooltip title={text("Zoom out")}>
        <Button
          size={size}
          icon={<ZoomOutOutlined />}
          disabled={disabled || zoom <= minZoom}
          onClick={handleZoomOut}
          aria-label={text("Zoom out")}
        />
      </Tooltip>

      <Tooltip title={text("Zoom")}>
        <Select
          size={size}
          value={Math.round(zoom * 100) / 100}
          onChange={(val) => onZoomChange(Number(val))}
          disabled={disabled}
          style={{ width: 88 }}
          options={options}
          aria-label={text("Zoom")}
        />
      </Tooltip>

      <Tooltip title={text("Zoom in")}>
        <Button
          size={size}
          icon={<ZoomInOutlined />}
          disabled={disabled || zoom >= maxZoom}
          onClick={handleZoomIn}
          aria-label={text("Zoom in")}
        />
      </Tooltip>

      {showReset ? (
        <Tooltip title={text("Reset zoom")}>
          <Button
            size={size}
            icon={<FullscreenOutlined />}
            disabled={disabled || Math.abs(zoom - 1) < 0.01}
            onClick={handleReset}
            aria-label={text("Reset zoom")}
          />
        </Tooltip>
      ) : null}
    </Flex>
  );
}
